import { useEffect, useRef } from "react";

// Runs `load()` while `enabled`, again when `deps` change, and again when
// `refreshKey` changes (socket refreshes). Only the refreshKey case is
// delayed, so a burst of refreshes turns into one request; a tab opening,
// a date change or a manual reload fetches at once. `load` receives an
// isCancelled() function so stale responses can be dropped.
export default function useDebouncedLoad(enabled, refreshKey, deps, load, delayMs = 800) {
  const keyRef = useRef(refreshKey);
  const enabledRef = useRef(false);

  useEffect(() => {
    const keyChanged = keyRef.current !== refreshKey;
    const wasEnabled = enabledRef.current;
    keyRef.current = refreshKey;
    enabledRef.current = enabled;
    if (!enabled) return undefined;

    let cancelled = false;
    const run = () => load(() => cancelled);
    const timer = keyChanged && wasEnabled ? setTimeout(run, delayMs) : null;
    if (!timer) run();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refreshKey, ...deps]);
}
